import { CandidateReferenceBadge } from "@components/candidates/CandidateReferenceBadge";
import type { TokenResolutions } from "@lib/types";
import type { ReactNode } from "react";
import { Link } from "react-router";

// Renders a single PII token as its resolved display value: candidate
// tokens (C5) become a link to the candidate, generic labels ([EMAIL_1])
// become their bound value, and anything unresolvable falls back to the
// literal token text.
export function TokenSpan({
  token,
  resolutions,
}: {
  token: string;
  resolutions: TokenResolutions;
}) {
  const candidate = resolutions.candidates[token];
  if (candidate) {
    // The token is `C{reference_number}` (e.g. C5), so the candidate number
    // is the token's digits — surface it as the reference badge.
    const referenceNumber = Number(token.slice(1));
    return (
      <>
        <Link to={candidate.path} className="font-medium text-accent hover:underline">
          {candidate.full_name}
        </Link>
        {Number.isFinite(referenceNumber) && (
          <>
            {" "}
            <CandidateReferenceBadge referenceNumber={referenceNumber} />
          </>
        )}
      </>
    );
  }

  const generic = resolutions.generic[token.replace(/^\[|\]$/g, "")];
  if (generic) {
    return <span>{generic}</span>;
  }

  return <span className="font-mono">{token}</span>;
}

const SPLIT_PATTERN = /(\[[A-Z][A-Z_]*_\d+\]|\bC\d+\b)/g;

// Split plain text on token boundaries and render each token through
// TokenSpan. For markdown surfaces, prefer a string pre-pass (see
// ChatMessage) — this helper is for plain-text rendering.
export function renderTextWithTokens(text: string, resolutions: TokenResolutions): ReactNode[] {
  return text.split(SPLIT_PATTERN).map((part, index) => {
    if (part.match(/^(\[[A-Z][A-Z_]*_\d+\]|C\d+)$/)) {
      // biome-ignore lint/suspicious/noArrayIndexKey: static split of a fixed string
      return <TokenSpan key={index} token={part} resolutions={resolutions} />;
    }
    return part;
  });
}
